import React from "react";
import { useAuth } from "../contexts/AuthContext";
import AccessDenied from "../pages/AccessDenied";
import UpgradePlan from "../pages/UpgradePlan";

/**
 * Enhanced ProtectedRoute component that handles both role and module-based authorization
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Component to render if authorized
 * @param {string[]} [props.roles] - Required roles for access
 * @param {string} [props.module] - Required module for access
 * @param {string} [props.feature] - Required feature for access
 * @param {boolean} [props.requireAll=false] - If true, user must have ALL specified roles/features
 * @param {string} [props.fallback="access-denied"] - Type of fallback component ("access-denied" or "upgrade-plan")
 * @param {React.ReactNode} [props.customFallback] - Custom fallback component
 */
const ProtectedRoute = ({
  children,
  roles = [],
  module,
  feature,
  requireAll = false,
  fallback = "access-denied",
  customFallback,
}) => {
  const { isAuthorized } = useAuth();

  const authConfig = {
    roles,
    module,
    feature,
    requireAll,
  };

  const hasAccess = isAuthorized(authConfig);

  if (!hasAccess) {
    // Return custom fallback if provided
    if (customFallback) {
      return customFallback;
    }

    // Return appropriate fallback based on type
    if (fallback === "upgrade-plan") {
      return <UpgradePlan />;
    }

    return <AccessDenied />;
  }

  return children;
};

export default ProtectedRoute;
